// CC: taken from plan-settings-domain.component.ts — simplified for onboarding wizard
import * as util from 'util';
import _ from 'lodash';
import {GeneralService} from '../../../services/generalService';
import {RouteService} from '../../../services/routeService';
import {NotificationService} from '../../../services/notificationService';
import {DICTIONARY} from '../../../dictionary';
import {Component, OnDestroy, OnInit} from "@angular/core";
import {ClipboardService} from "ngx-clipboard";
import {connectDomain, subscribe} from 'entrijs';

@Component({
	selector: 'onboarding-domains-component',
	templateUrl: './onboarding-domains.component.html',
})
export class OnboardingDomainsComponent implements OnInit, OnDestroy {
	dic = DICTIONARY;
	userDomains = [];
	loadingInProcess = true;
	reverifyAllInProcess = false;
	entriUnsubscribers = [];
	userInfo;
	addDomainPopup;
	private verifyRunId = 0;

	constructor(public gs: GeneralService,
				private rs: RouteService,
				private ns: NotificationService,
				private clipboard: ClipboardService) {
	}

	ngOnDestroy() {
		this.verifyRunId++;
		this.unsubscribeEntriEvents();
	}

	ngOnInit() {
		this.gs.getUserInfo(false, (userInfo) => {
			this.userInfo = userInfo;
		});
		this.getDomains();
	}

	getDomains = () => {
		this.loadingInProcess = true;
		this.rs.getPlanDomains().then((response) => {
			this.userDomains = response;
			this.loadingInProcess = false;
			this.verifyDomainsOnLoad();
		}, () => {
			this.loadingInProcess = false;
		});
	};

	// CC: taken from plan-settings-domain.component.ts -> addActiveDomain + addDomain
	openAddDomain = () => {
		this.addDomainPopup = {
			show: true,
			applyInProcess: false,
			domainName: '',
			autoVerify: false,
			errMsg: '',
		};
	};

	addDomain = () => {
		const domain = this.addDomainPopup.domainName;
		if (!domain) {
			return;
		}
		this.addDomainPopup.applyInProcess = true;
		const autoVerify = this.addDomainPopup.autoVerify;
		this.rs.addDomain(domain).then((response) => {
			this.userDomains = response;
			this.addDomainPopup.show = false;

			if (autoVerify) {
				const newDomainObj = _.find<any>(this.userDomains, domainObj => domainObj.domain === domain);
				if (newDomainObj) {
					this.connectEntriDomain(newDomainObj);
				}
			}
		}, err => {
			this.addDomainPopup.applyInProcess = false;
			if (err?.data?.message) {
				this.addDomainPopup.errMsg = err.data.message;
			}
		});
	};

	// CC: taken from plan-settings-domain.component.ts -> verifyDomainsOnLoad
	verifyDomainsOnLoad = async () => {
		const runId = ++this.verifyRunId;
		for (const domainObj of this.userDomains || []) {
			if (runId !== this.verifyRunId) {
				return;
			}
			if (!domainObj.verified) {
				await this.isDomainIdentityVerified(domainObj);
			}
			if (!domainObj.dkim_verified) {
				await this.isDomainDkimVerified(domainObj);
			}
			if (!domainObj.mail_from_verified) {
				await this.isDomainMailFromVerified(domainObj);
			}
			await this.isDomainSpfVerified(domainObj);
		}
	};

	// CC: taken from plan-settings-domain.component.ts -> reverifyAllDomains
	reverifyAllDomains = async () => {
		if (this.reverifyAllInProcess || this.loadingInProcess || !this.userDomains?.length) {
			return;
		}
		this.reverifyAllInProcess = true;
		const runId = ++this.verifyRunId;
		try {
			for (const domainObj of this.userDomains) {
				if (runId !== this.verifyRunId) {
					return;
				}
				await this.isDomainIdentityVerified(domainObj);
				await this.isDomainDkimVerified(domainObj);
				await this.isDomainMailFromVerified(domainObj);
				await this.isDomainSpfVerified(domainObj);
			}
		}
		finally {
			this.reverifyAllInProcess = false;
		}
	};

	// CC: taken from plan-settings-domain.component.ts -> verifyDomainRecord
	verifyDomainRecord = (domainObj, type) => {
		return this.rs.isDomainVerified(domainObj.domain, {type}).then(isVerified => isVerified === true, () => null);
	};

	isDomainIdentityVerified(domainObj) {
		return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.identity).then(isVerified => {
			if (isVerified !== null) {
				domainObj.verified = isVerified;
			}
		});
	}

	isDomainDkimVerified(domainObj) {
		return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.dkim).then(isVerified => {
			if (isVerified !== null) {
				domainObj.dkim_verified = isVerified;
			}
		});
	}

	isDomainSpfVerified(domainObj) {
		return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.spf).then(isVerified => {
			if (isVerified !== null) {
				domainObj.spf_verified = isVerified;
			}
		});
	}

	isDomainMailFromVerified(domainObj) {
		return this.verifyDomainRecord(domainObj, this.dic.CONSTANTS.domainVerify.mailFrom).then(isVerified => {
			if (isVerified !== null) {
				domainObj.mail_from_verified = isVerified;
			}
		});
	}

	// CC: taken from plan-settings-domain.component.ts -> connectEntriDomain
	connectEntriDomain = (domainObj) => {
		if (!domainObj?.domain) {
			return;
		}

		this.unsubscribeEntriEvents();

		this.entriUnsubscribers.push(subscribe('onSuccess', (event) => {
			if (event.domain !== domainObj.domain) {
				return;
			}

			if (!event.success) {
				this.ns.showWarnMessage(util.format(this.dic.ERRORS.domainNotVerified, domainObj.domain));
				return;
			}

			const assignData = {
				action: this.dic.CONSTANTS.domainsActions.updateProvider.value,
				dns_provider: event.provider,
				auto_update: (event.setupType || '').toLowerCase() === 'automatic'
			};
			this.rs.updateDomain(domainObj.domain, assignData).then(() => {
				this.ns.showInfoMessage(util.format(this.dic.MESSAGES.domainVerified, domainObj.domain));
			}, err => {
				if (err?.data?.message && !err.data.display_bar) {
					this.ns.showErrorMessage(err.data.message);
				}
			});
		}));

		this.entriUnsubscribers.push(subscribe('onEntriClose', () => {
			this.unsubscribeEntriEvents();
		}));

		this.entriUnsubscribers.push(subscribe('entriConfigError', () => {
			this.ns.showWarnMessage(util.format(this.dic.ERRORS.domainNotVerified, domainObj.domain));
			this.unsubscribeEntriEvents();
		}));

		const assignData = {
			action: this.dic.CONSTANTS.domainsActions.verifyEntri.value
		};

		this.rs.updateDomain(domainObj.domain, assignData).then(entriConfig => {
			connectDomain({
				applicationId: entriConfig.applicationId,
				token: entriConfig.token,
				dnsRecords: entriConfig.dnsRecords,
				prefilledDomain: domainObj.domain,
				applicationName: 'Trustifi',
				userId: entriConfig.userId, // must equal the userId the backend signed the token with, otherwise Entri forces the manual screen (same fix as plan-settings-domain 8588e01fae)
				checkConflicts: true,
				useNewUI: true
			}).catch(() => {
				this.ns.showWarnMessage(util.format(this.dic.ERRORS.domainNotVerified, domainObj.domain));
				this.unsubscribeEntriEvents();
			});
		}, err => {
			this.unsubscribeEntriEvents();
			if (err?.data?.message && !err.data.display_bar) {
				this.ns.showErrorMessage(err.data.message);
			}
		});
	};

	// CC: taken from plan-settings-domain.component.ts -> unsubscribeEntriEvents
	unsubscribeEntriEvents = () => {
		const pending = this.entriUnsubscribers;
		this.entriUnsubscribers = [];
		pending.forEach(promise => {
			Promise.resolve(promise).then(unsubscribe => {
				if (typeof unsubscribe === 'function') {
					unsubscribe();
				}
			});
		});
	};

	toggleDnsRecords = (domainObj) => {
		domainObj.isExpanded = !domainObj.isExpanded;
	};

	copyText(text) {
		this.clipboard.copy(text);
	}

	// CC: taken from plan-settings-domain.component.ts -> getDomainDkimAndTokenCsv
	getDomainDkimAndTokenCsv(domainObj) {
		if (!domainObj) {
			return;
		}

		let csvString = "TXT Records:\n";
		csvString += 'Host,Type,Value\n';
		csvString += `_amazonses,TXT,${domainObj.data.verify_token}\n`;
		csvString += `trustifi,TXT,"v=spf1 include:amazonses.com ~all"\n`;
		csvString += '\n';

		csvString += 'CNAME Records:\n';
		csvString += 'Host,Type,Pointer\n';
		domainObj.data.dkim.forEach((dkim) => {
			csvString += `${dkim}._domainkey,CNAME,${dkim}.dkim.amazonses.com\n`;
		});
		csvString += '\n';

		csvString += 'MX Records:\n';
		csvString += 'Host,Type,Pointer,Priority\n';
		csvString += `trustifi,MX,feedback-smtp.us-east-1.amazonses.com,10\n`;

		this.gs.exportCsv(csvString, `domain_${domainObj.domain}_data.csv`);
	}

	isAllVerified(domainObj) {
		return domainObj.verified && domainObj.dkim_verified && domainObj.mail_from_verified && domainObj.spf_verified;
	}
}
